import { getSportBySlug, isOddsScreenLeagueId } from "@/lib/odds-screen/constants";

export const POST_LOGIN_DEFAULT = "/home";

export const LAST_TOOL_COOKIE_NAME = "upsider_last_tool";
export const LAST_TOOL_COOKIE_MAX_AGE = 60 * 60 * 24 * 180;

const LAST_TOOL_PATHS = [
  "/optimizer/props",
  "/optimizer",
  "/ev-sniper",
  "/edge-finder",
  "/whale-watch",
  "/arbitrage",
  "/low-hold",
  "/odds-screen",
  "/prop-discrepancies",
];

const UNSAFE_REDIRECT_CHAR_RE = /[\\\u0000-\u001f]/;

const UNSAFE_REDIRECT_PATH_CHAR_RE = /[\s\u007f-\u009f]/;

const CHECKOUT_REDIRECT_MAX_LENGTH = 200;

type RedirectValidationCandidate = {
  original: string;
  decoded: string;
};

function getRedirectValidationCandidate(
  explicit: string | null | undefined,
): RedirectValidationCandidate | null {
  if (!explicit) return null;
  if (!explicit.trim()) return null;

  let decoded: string;
  try {
    decoded = decodeURIComponent(explicit);
  } catch {
    return null;
  }

  if (!decoded.trim()) return null;

  if (
    UNSAFE_REDIRECT_CHAR_RE.test(explicit) ||
    UNSAFE_REDIRECT_CHAR_RE.test(decoded) ||
    UNSAFE_REDIRECT_PATH_CHAR_RE.test(pathnameOnly(explicit)) ||
    UNSAFE_REDIRECT_PATH_CHAR_RE.test(pathnameOnly(decoded))
  ) {
    return null;
  }

  return { original: explicit, decoded };
}

export function decodeRedirectCandidate(explicit: string | null | undefined): string | null {
  return getRedirectValidationCandidate(explicit)?.original ?? null;
}

function isSafeRelativeRedirectTarget(candidate: string): boolean {
  return (
    candidate.startsWith("/") &&
    !candidate.startsWith("//") &&
    !new URL(candidate, "https://upside.invalid").pathname.startsWith("//")
  );
}

export function sanitizeRelativeRedirectTarget(
  explicit: string | null | undefined,
  fallback = POST_LOGIN_DEFAULT,
): string {
  const candidate = getRedirectValidationCandidate(explicit);

  return candidate &&
    isSafeRelativeRedirectTarget(candidate.original) &&
    isSafeRelativeRedirectTarget(candidate.decoded)
    ? candidate.original
    : fallback;
}

export function checkoutRedirectTarget(value: unknown): string | null {
  if (typeof value !== "string" || value.length > CHECKOUT_REDIRECT_MAX_LENGTH) return null;
  return sanitizeRelativeRedirectTarget(value, "") || null;
}

function hasExplicitRedirect(explicit: string | null | undefined): boolean {
  return typeof explicit === "string" && explicit.trim().length > 0;
}

function pathnameOnly(target: string): string {
  const queryIndex = target.search(/[?#]/);
  return queryIndex === -1 ? target : target.slice(0, queryIndex);
}

function normalizePathname(pathname: string): string {
  return pathname.length > 1 && pathname.endsWith("/")
    ? pathname.slice(0, -1)
    : pathname;
}

export function lastToolCookieOptions(secure: boolean): {
  httpOnly: true;
  secure: boolean;
  sameSite: "lax";
  path: "/";
  maxAge: number;
} {
  return {
    httpOnly: true,
    secure,
    sameSite: "lax",
    path: "/",
    maxAge: LAST_TOOL_COOKIE_MAX_AGE,
  };
}

export function lastToolPathFromPathname(pathname: string): string | null {
  const normalized = normalizePathname(pathnameOnly(pathname));

  for (const prefix of LAST_TOOL_PATHS) {
    if (normalized === prefix) return normalized;
    if (prefix === "/odds-screen" && normalized.startsWith("/odds-screen/")) {
      const rest = normalized.slice("/odds-screen/".length);
      if (rest && !rest.includes("/") && (isOddsScreenLeagueId(rest) || getSportBySlug(rest))) return normalized;
    }
  }

  return null;
}

export function resolveLastToolPath(value: string | null | undefined): string | null {
  const sanitized = sanitizeRelativeRedirectTarget(value, "");
  if (!sanitized) return null;
  return lastToolPathFromPathname(sanitized);
}

export function resolvePostLoginRedirect(
  explicit: string | null | undefined,
  lastTool?: string | null,
): string {
  if (hasExplicitRedirect(explicit)) {
    return sanitizeRelativeRedirectTarget(explicit, POST_LOGIN_DEFAULT);
  }

  return resolveLastToolPath(lastTool) ?? POST_LOGIN_DEFAULT;
}

const PURCHASE_SECTIONS = new Set(["checkout", "start", "pricing", "signup"]);

export function newAccountRedirect(explicit: string | null | undefined): string {
  const target = sanitizeRelativeRedirectTarget(explicit, "");
  const section = normalizePathname(pathnameOnly(target)).split("/")[1];
  if (!target || section === "signin") return "/pricing";
  if (PURCHASE_SECTIONS.has(section)) return target;
  return `/pricing?redirect_to=${encodeURIComponent(target)}`;
}
